import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Tab,
  Tabs,
  Typography,
  alpha,
} from '@mui/material';
import { Icon } from '@iconify/react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import Page from 'src/components/Page';
import { downloadTextFile, formatMoney, payrollImportService } from 'src/services/payroll';
import type { PayrollSnapshot } from 'src/types/payroll.types';
import { invalidateStudentFinancialQueries } from 'src/utils/queryInvalidation';
import { PaySummaryCards } from './components/PaySummaryCards';
import {
  PayrollRowsTable,
  isPayableStatus,
  isProblemStatus,
} from './components/PayrollRowsTable';
import { PaycheckStub } from './components/PaycheckStub';

export default function PayrollImportDetail() {
  const { importId } = useParams<{ importId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState(0);
  const [stub, setStub] = useState<PayrollSnapshot | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['payroll-import', importId],
    queryFn: () => payrollImportService.getImport(importId as string),
    enabled: !!importId,
  });

  const retryMutation = useMutation({
    mutationFn: () =>
      payrollImportService.approveImport(importId as string, { retryFailedOnly: true }),
    onSuccess: async (result) => {
      toast.success(`Retried: ${result.posted} posted, ${result.failed} failed`);
      await invalidateStudentFinancialQueries(queryClient, {
        extraKeys: [
          ['payroll-import', importId],
          ['payroll-imports'],
        ],
      });
    },
    onError: (err: any) => toast.error(err?.message || 'Retry failed'),
  });

  const deleteMutation = useMutation({
    mutationFn: () => payrollImportService.deleteImport(importId as string),
    onSuccess: () => {
      toast.success('Scholar Dollar Pay import deleted');
      setDeleteOpen(false);
      queryClient.invalidateQueries({ queryKey: ['payroll-imports'] });
      navigate('/admin/payroll');
    },
    onError: (err: any) => toast.error(err?.message || 'Could not delete payroll import'),
  });

  const readyRows = useMemo(() => data?.rows.filter((r) => isPayableStatus(r.match_status)) || [], [data]);
  const problemRows = useMemo(
    () => data?.rows.filter((r) => isProblemStatus(r.match_status)) || [],
    [data]
  );
  const postedRows = useMemo(() => data?.rows.filter((r) => r.match_status === 'posted') || [], [data]);
  const failedRows = useMemo(
    () => data?.rows.filter((r) => r.match_status === 'post_failed') || [],
    [data]
  );

  const tabRows =
    tab === 0 ? (postedRows.length ? postedRows : readyRows) : tab === 1 ? problemRows : failedRows;

  if (isLoading) {
    return (
      <Page title="Scholar Dollar Pay import">
        <Box sx={{ py: 12, textAlign: 'center' }}>
          <CircularProgress />
        </Box>
      </Page>
    );
  }

  if (error || !data) {
    return (
      <Page title="Scholar Dollar Pay import">
        <Container maxWidth="md" sx={{ py: 6 }}>
          <Alert severity="error">Could not load this payroll import.</Alert>
          <Button sx={{ mt: 2, textTransform: 'none' }} onClick={() => navigate('/admin/payroll')}>
            Back to hub
          </Button>
        </Container>
      </Page>
    );
  }

  return (
      <Page title="Scholar Dollar Pay import detail">
        <Container
          maxWidth={false}
          sx={{ width: '100%', px: 1, mx: 0 }}
        >
        <Button
          startIcon={<Icon icon="mdi:arrow-left" />}
          onClick={() => navigate('/admin/payroll')}
          sx={{ textTransform: 'none', mb: 0.25, color: 'text.secondary', p: 0, fontSize: '0.75rem' }}
        >
          Scholar Dollar Pay
        </Button>

        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          spacing={1}
          sx={{ mb: 1.5 }}
        >
          <Box>
            <Typography variant="h5" fontWeight={800} color="#1A202C">
              {data.file_name || 'Scholar Dollar Pay import'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {data.pay_period_key.replace('_', ' → ')} · status{' '}
              <Box component="span" sx={{ fontWeight: 700, textTransform: 'capitalize' }}>
                {data.status}
              </Box>
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Button
              color="error"
              startIcon={<Icon icon="mdi:delete-outline" />}
              disabled={postedRows.length > 0 || deleteMutation.isPending}
              onClick={() => setDeleteOpen(true)}
              sx={{ textTransform: 'none', borderRadius: 2 }}
              title={postedRows.length > 0 ? 'Posted imports cannot be deleted' : 'Delete import'}
            >
              Delete import
            </Button>
            {problemRows.length > 0 && (
              <Button
                startIcon={<Icon icon="mdi:download" />}
                sx={{ textTransform: 'none', borderRadius: 2 }}
                onClick={() =>
                  downloadTextFile(
                    `payroll-problems-${data.id.slice(0, 8)}.csv`,
                    payrollImportService.exportProblemRowsCsv(problemRows)
                  )
                }
              >
                Export problems
              </Button>
            )}
            {failedRows.length > 0 && (
              <Button
                variant="contained"
                disabled={retryMutation.isPending}
                onClick={() => retryMutation.mutate()}
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  borderRadius: 2,
                  bgcolor: '#EDB42E',
                  color: '#1A1A1A',
                }}
              >
                Retry failures
              </Button>
            )}
            {data.status === 'preview' && readyRows.length > 0 && (
              <Button
                variant="contained"
                onClick={() => navigate('/admin/payroll/import')}
                sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2, bgcolor: '#297041' }}
              >
                Continue in wizard
              </Button>
            )}
            {postedRows.length > 0 && (
              <Button
                variant="outlined"
                startIcon={<Icon icon="mdi:file-chart-outline" />}
                onClick={() => navigate('/admin/payroll/tax-day')}
                sx={{
                  textTransform: 'none',
                  borderRadius: 2,
                  fontWeight: 700,
                  color: '#805AD5',
                  borderColor: '#805AD5',
                  '&:hover': { borderColor: '#6B46C1', bgcolor: alpha('#805AD5', 0.04) },
                }}
              >
                Tax Day YTD
              </Button>
            )}
          </Stack>
        </Stack>

        <Box sx={{ mb: 2.25 }}>
          <PaySummaryCards
            matched={data.totals.matched_count}
            problems={data.totals.error_count}
            warnings={data.totals.warning_count}
            totalGross={data.totals.total_gross}
            totalTax={data.totals.total_tax}
            totalNet={data.totals.total_net}
          />
        </Box>

        {data.note && (
          <Alert
            icon={<Icon icon="mdi:note-text-outline" width={22} />}
            severity="info"
            sx={{
              mb: 2.25,
              borderRadius: 2,
              alignItems: 'flex-start',
              '& .MuiAlert-message': { minWidth: 0, width: '100%' },
            }}
          >
            <Typography variant="caption" fontWeight={800} sx={{ display: 'block', mb: 0.5 }}>
              Import note
            </Typography>
            <Typography
              variant="body2"
              sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', lineHeight: 1.55 }}
            >
              {data.note}
            </Typography>
          </Alert>
        )}

        {postedRows.length > 0 && (
          <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
            {postedRows.length} paycheck{postedRows.length === 1 ? '' : 's'} posted · Net:{' '}
            {formatMoney(postedRows.reduce((s, r) => s + Number(r.net), 0))} · Tax withheld:{' '}
            {formatMoney(postedRows.reduce((s, r) => s + Number(r.tax_withheld), 0))} recorded for Tax Day
          </Alert>
        )}

        <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #E2E8F0' }}>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{
              px: { xs: 1, sm: 2 },
              borderBottom: '1px solid #E2E8F0',
              bgcolor: alpha('#297041', 0.03),
              '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 },
            }}
          >
            <Tab label={postedRows.length ? `Posted (${postedRows.length})` : `Ready (${readyRows.length})`} />
            <Tab label={`Problems (${problemRows.length})`} />
            <Tab label={`Failed (${failedRows.length})`} />
          </Tabs>

          <PayrollRowsTable rows={tabRows} />

          {tab === 0 && postedRows.length > 0 && (
            <Box sx={{ p: 2, borderTop: '1px solid #E2E8F0' }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Click a posted row’s transaction to open the paycheck stub (via row transaction id).
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={1}>
                {postedRows
                  .filter((r) => r.transaction_id)
                  .slice(0, 12)
                  .map((r) => (
                    <Button
                      key={r.id}
                      size="small"
                      variant="outlined"
                      sx={{ textTransform: 'none', borderRadius: 2 }}
                      onClick={async () => {
                        const snap = await payrollImportService.getPaycheckStub(r.transaction_id!);
                        if (snap) setStub(snap);
                        else toast.info('No stub snapshot on this transaction');
                      }}
                    >
                      Stub: {r.first_name} {r.last_name}
                    </Button>
                  ))}
              </Stack>
            </Box>
          )}
        </Card>

        <Dialog
          open={!!stub}
          onClose={() => setStub(null)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
        >
          {stub && (
            <PaycheckStub
              snapshot={stub}
              studentName={`${stub.first_name || ''} ${stub.last_name || ''}`.trim()}
              onClose={() => setStub(null)}
            />
          )}
        </Dialog>

        <Dialog
          open={deleteOpen}
          onClose={() => !deleteMutation.isPending && setDeleteOpen(false)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle sx={{ fontWeight: 800 }}>Delete this payroll import?</DialogTitle>
          <DialogContent>
            <Typography color="text.secondary" lineHeight={1.6}>
              This will permanently remove the import record and its preview rows. This action is only available
              because no paychecks have been posted from this import.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5 }}>
            <Button
              disabled={deleteMutation.isPending}
              onClick={() => setDeleteOpen(false)}
              sx={{ textTransform: 'none' }}
            >
              Keep import
            </Button>
            <Button
              color="error"
              variant="contained"
              disabled={deleteMutation.isPending}
              onClick={() => deleteMutation.mutate()}
              startIcon={<Icon icon="mdi:delete-outline" />}
              sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2 }}
            >
              {deleteMutation.isPending ? 'Deleting…' : 'Delete import'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Page>
  );
}
